import { useCreativeStrategyStore } from '@/stores/creativeStrategy';

export function PackCitationsPanel({ clientUid, onOpenItem }: { clientUid: string; onOpenItem: (id: string) => void }) {
  const { citations } = useCreativeStrategyStore();

  if (!citations) return <div className="text-sm text-slate-400">Loading citations…</div>;
  if (!citations.available) {
    return (
      <div className="max-w-2xl rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
        Citation telemetry is not available from this API instance{citations.reason ? `: ${citations.reason}` : '.'}
        <div className="text-xs text-slate-400 mt-1">
          Angles record the pack ids they drew on (evidence_ids, template_ref); this view aggregates them across the client's briefs.
        </div>
      </div>
    );
  }

  const top = Object.entries(citations.items).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-6">
      <div className="text-sm text-slate-600">
        {citations.angles.length} citing angle{citations.angles.length === 1 ? '' : 's'} across {citations.briefs_scanned ?? 0} brief
        {citations.briefs_scanned === 1 ? '' : 's'} for {clientUid}.
      </div>

      {top.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {top.map(([id, n]) => (
            <button
              key={id}
              onClick={() => onOpenItem(id)}
              className="inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-brand-teal/50"
            >
              {id} <span className="text-slate-400 tabular-nums">×{n}</span>
            </button>
          ))}
        </div>
      )}

      {citations.angles.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">Brief</th>
                <th className="text-left px-4 py-2 font-medium">Angle hook</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Cited</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {citations.angles.map((a) => (
                <tr key={a.angle_id}>
                  <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{a.created_at?.slice(0, 10) ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-700 max-w-[14rem] truncate" title={a.brief_id}>{a.brief_name}</td>
                  <td className="px-4 py-2 text-slate-800 max-w-md">{a.hook}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{a.status ?? '—'}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {a.evidence_ids.map((id) => (
                        <button key={id} onClick={() => onOpenItem(id)} className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 hover:bg-brand-teal/10">
                          {id}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
